import gql from 'graphql-tag';
import { MobXProviderContext } from 'mobx-react';
import React from 'react';
import PropTypes from 'prop-types';
import { updateUserAttribute, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { AuthProvider as DescopeProvider } from '@descope/react-sdk';
import { isProductionEnv as checkIsProductionEnv } from '@ourbranch/env-helpers';

import awsConfig from 'aws-exports';
import { withToast } from 'core/components/toast';
import { CognitoPermissionGroups } from 'core/helpers/cognito-permission-groups';
import { initializeFrontChatAndIdentify, identifyUserInUpscope, identifyUserInAppcues } from 'configure-services';
import { AuthContext } from './auth.context';
import { identify, track } from 'core/helpers/analytics';
import { GET_SPIN_THE_WHEEL_ELIGIBILITY, GET_SPIN_THE_WHEEL_HISTORY } from 'core/store/offer-store/offer-queries';
import { appConfig, isBranchEnv } from 'brand-config';
import oAuthStorage from 'core/components/auth/oauth-storage';
import { resolveShowAgencyPortal } from './portal-access';
import { diffPermissionGroups } from 'core/helpers/diff-permission-groups';

const isProductionEnv = checkIsProductionEnv(import.meta.env.VITE_STACKERY_ENVIRONMENT);

function getPermissions(userAttributes) {
  const groups = userAttributes['cognito:groups'] || [];

  if (!groups.length) {
    return {
      isService: false,
      isSales: false,
      canEdit: false
    };
  }

  // licensed agent groups

  const isService = groups.includes('Service');
  const isSales = groups.includes('InternalSales') || groups.includes('ExternalSales');
  const isInternalSales = groups.includes('InternalSales');
  const isExternalSales = groups.includes('ExternalSales');
  const isTeamLeader = groups.includes('TeamLeaders');

  const parseCustomAttribute = (attr) => {
    try {
      return JSON.parse(attr);
    } catch {
      return [];
    }
  };

  // logic surrounding licensed and unlicensed states for external users
  const isAgency =
    !!parseCustomAttribute(userAttributes['custom:affinity_groups']).length ||
    !!parseCustomAttribute(userAttributes['custom:affinity_group_regex']).length;
  const parsedAllowedStates = parseCustomAttribute(userAttributes['custom:allowed_states']);
  const parsedUnlicensedStates = parseCustomAttribute(userAttributes['custom:unlicensed_states']);

  // individual features access
  const canReinstate = groups.includes('Reinstatement');
  const canRescindCancellation = groups.includes('RescindCancellation');
  const canSeeFullOfferDetails = groups.includes('CompleteOfferDetails');
  const canScrubIncidents = groups.includes('ScrubIncidents');
  const canAddIncidents = groups.includes('AddIncidents');
  const canModifyAffinityAndLeadSource = groups.includes('ModifyAffinityCodes');
  const canBackDate = groups.includes('CanBackDate');
  const canModifyBillingId = groups.includes('ModifyBillingID');
  const canViewClarionDoorData = groups.includes('ViewClarionDoorData');
  const canAddCarsManually = groups.includes('AddCarsManually');
  const canAutoRenew = groups.includes('CanAutoRenew');
  const canAddHoldCards = groups.includes('CanAddHoldCards');
  const canChangeExclusions = groups.includes('CanChangeExclusions');
  const hasUnlicensedGroup = groups.includes('Unlicensed');
  const canToggleEmployeeDiscount = groups.includes('CanToggleEmployeeDiscount');
  const canClearUDRs = groups.includes('CanClearUDRs');
  const canEditHomeAddress = groups.includes('CanEditHomeAddress');
  const canEditEffectiveDate = groups.includes('CanEditEffectiveDate');
  const canManuallyChargeFee = groups.includes('CanManuallyChargeFee');
  const canQuoteBranchPlus = groups.includes('CanQuoteBranchPlus');
  const canClearUWFormRejections = groups.includes('CanClearUWFormRejections');
  const canManuallyChargePolicy = groups.includes('CanManuallyChargePolicy');
  const canRewritePolicy = groups.includes('CanRewritePolicy');
  const canRemoveExcludedDriver = groups.includes('CanRemoveExcludedDriver');
  const canCancelReasonCNIP = groups.includes('CanCancelReasonCNIP');
  const canCancelReasonCNMS = groups.includes('CanCancelReasonCNMS');
  const canCancelReasonCNPY_X = groups.includes('CanCancelReasonCNPYX');
  const canRejectPIP = groups.includes('CanRejectPIP');
  const showAgencyPortal = groups.includes('ShowAgencyPortal');
  const hasCanQuoteGroup = groups.includes('CanQuote');
  const hasCanViewPolicyGroup = groups.includes('CanViewPolicy');
  const canDownloadAnonDocs = groups.includes('CanDownloadAnonDocs');
  const canReinstateCNPY = groups.includes('CanReinstateCNPY');
  const canToggleCreditCheck = groups.includes('CanToggleCreditCheck');
  const canEditBillingDay = groups.includes('CanEditBillingDay');
  const canToggleCHDiscount = groups.includes('CanToggleCHDiscount');
  const canToggleInventoryDiscount = groups.includes('CanToggleInventoryDiscount');
  const canEditPolicyMPD = groups.includes('CanEditPolicyMPD');
  const canDelayPaymentOrRefund = groups.includes('CanDelayPaymentOrRefund');
  const canManageBillingHolds = groups.includes('CanManageBillingHolds');
  const canSendPaperDocs = groups.includes('CanSendPaperDocs');
  const canEditFeeCheckbox = groups.includes('CanEditFeeCheckbox');
  const canOverrideGaragingAddress = groups.includes('CanOverrideGaragingAddress');
  const canSwapApplicants = groups.includes('CanSwapApplicants');
  const canViewCarrierInitiatedCancels = groups.includes('CanViewCarrierInitiatedCancels');
  const canSellAndService = groups.includes('ExternalSales') && parsedAllowedStates?.length > 0;
  const canCancelReasonNACP = groups?.includes('CanCancelReasonNACP');
  const canEditUneditablePropertyDetailsFields = groups?.includes('CanEditUneditablePropertyDetails');
  const canResetOnboardingTasks = groups?.includes('CanResetOnboardingTasks');
  const canCancelOnboardingTasks = groups?.includes('CanCancelOnboardingTasks');
  const canViewInternalTasks = groups?.includes('CanViewInternalTasks');
  const canViewUWCancelCodes = groups?.includes('CanViewUWCancelCodes');
  const canToggleIsAgencyPolicy = groups?.includes('CanToggleIsAgencyPolicy');
  const canRefundStripePayment = groups?.includes('CanRefundStripePayment');
  const canRescindOrReinstateGSNICPolicy = groups?.includes('CanRescindOrReinstateGSNICPolicy');
  const canRemoveFinalPaymentMethod = groups?.includes('CanRemoveFinalPaymentMethod');
  const canBypassDriversLicenseCheck = groups?.includes('CanBypassDriversLicenseCheck');
  const canEditCoverageA = groups?.includes('CanEditCoverageA');
  const canSeeMarketTierCard = groups?.includes('CanSeeMarketTierCard');
  const canWriteMultipleAffinityCodes = groups?.includes('CanWriteMultipleAffinityCodes');
  const canEditDriversName = groups?.includes('CanEditDriversName');
  const canModifyNonRatedIndividual = groups?.includes('CanModifyNonRatedIndividual');
  const canRevertPolicyDetails = groups?.includes('CanRevertPolicyDetails');
  const canToggleHomeRepairsRequested = groups?.includes('CanToggleHomeRepairsRequested');
  const canAccessProtectedUDDActions = groups.includes('canAccessProtectedUDDActions');
  const canEditPriorCarrierDetails = groups.includes('CanEditPriorCarrierDetails');
  const canSkipOfferUnderwritingRules = groups.includes('CanSkipOfferUnderwritingRules');
  const canEditFarmPolicyAdjustment = groups.includes('CanEditFarmPolicyAdjustment');
  const canApproveUWReview = groups.includes('CanApproveUWReview');
  const canDeleteInternalDocuments = groups.includes('CanDeleteInternalDocuments');
  const canOverrideAllRejections = groups.includes('CanOverrideAllRejections');

  // Lets non-agency Branch employees bind directly (see credit card / ACH form)
  // on staff sites that would otherwise force them into the Purchase Link flow.
  const canBindDirectly = groups.includes('CanBindDirectly');

  // can view both offers and policies but will not allow editing
  const viewOnly = groups.includes('ViewOnly');

  // Users that are not licensed in any states and therefore should only have a populated list of unlicensed states alongside the Unlicensed cognito group
  const isUnlicensed = hasUnlicensedGroup && Boolean(parsedUnlicensedStates?.length && !parsedAllowedStates.length);

  // Users that are not licensed in any states will have access to editing only unlicensed fields on offers/policies.
  // Alongside the Unlicensed group, they should also have populated list of unlicensed states.
  // The form field component logic takes care of disabling the licensed fields for these users.
  const canEditPolicies = (isTeamLeader || isSales || isService || isUnlicensed) && !viewOnly;
  const canEditOffers = (isTeamLeader || isSales || isService || isUnlicensed || hasCanQuoteGroup) && !viewOnly;
  const canViewOffers = viewOnly || canEditOffers;
  const canViewPolicies = viewOnly || hasCanViewPolicyGroup || canEditPolicies;

  // tools permissions:
  const canViewTools = groups.includes('CanViewTools');
  const canViewAffinityTools = groups.includes('toolsCanViewAffinityTools');
  const canViewStaffDashboard = groups.includes('toolsStaffDashboard');
  const canGenerateDescopePasskeyReport = groups.includes('toolsCanGenerateDescopePasskeyReport');
  const canResetStaffUserPassword = groups.includes('toolsStaffDashboardResetPassword');
  const canDisableStaffUser = groups.includes('toolsStaffDashboardDisableUser');
  const toolsDeleteRenewalSegment = groups.includes('toolsDeleteRenewalSegment');
  const toolsCreateUddReviewTask = groups.includes('toolsCreateUddReviewTask');
  const canCreateStripeCharge = groups.includes('toolsCreateStripeCharge');
  const canCreateChargedeskInvoice = groups.includes('toolsCreateChargedeskInvoice');
  const canCreateChargedeskRefundInvoice = groups.includes('toolsChargedeskRefundInvoice');
  const canBulkDisableStaffUsers = groups.includes('toolsStaffUsersBulkDisable');
  const canRunAddressesThroughCherre = groups.includes('toolsRunAddressesThroughCherre');
  const canRegeneratePolicyForm = groups.includes('toolsRegeneratePolicyForm');
  const canRegeneratePolicyCancelForm = groups.includes('toolsRegeneratePolicyCancelForm');
  const canUpdateChargedeskRecord = groups.includes('toolsChargedeskUpdateRecord');
  const canUpdateChargedeskRecordPolicyOrBillingId = groups.includes('toolsCDUpdateRecordLimited');
  const canGenerateRenewal = groups.includes('toolsCanGenerateRenewal');
  const canRunRenewalUnderwriting = groups.includes('toolsCanRunRenewalUnderwriting');
  const canUpdateStaffUser = groups.includes('toolsStaffDashboardUpdateUser');
  const canViewEditGroups = groups.includes('toolsViewEditUserGroups');
  const canUpdatePolicyRep = groups.includes('toolsUpdatePolicyRep');
  const canAddPermissionGroups = groups.includes('toolsAddPermissionGroups');
  const canCreateStaffUser = groups.includes('toolsCreateStaffUser');
  const canCreateFrontTicket = groups.includes('toolsCreateFrontTicket');
  const canViewStateRateChangeDashboard = groups.includes('toolsStateRateChangeDashboard');
  const canViewMoratoriumDashboard = groups.includes('toolsCanViewMoratoriumDashboard');
  const canImportPoliciesToSnapsheet = groups.includes('toolsImportPoliciesToSnapsheet');
  const canImportPoliciesToIvans = groups.includes('toolsImportPoliciesToIvans');
  const canAddCatTagsToClaims = groups.includes('toolsCanAddCatTagsToClaims');
  const canRunTechTasks = groups.includes('toolsCanRunTechTasks');
  const canCopyPolicy = groups.includes('toolsCanCopyPolicy');
  const canSetMemberAffinity = groups.includes('toolsCanSetMemberAffinity');
  const canEnableStaffUser = groups.includes('toolsCanEnableStaffUser');
  const canResetPasskeys = groups.includes('toolsCanResetPasskeys');
  const canPullDrivingHistoryFromCSV = groups.includes('toolsCanPullDrivingHistory');
  const canSetHasAutoPolicy = groups.includes('toolsCanSetHasAutoPolicy');
  const canUpdateBillingId = groups.includes('toolsCanUpdateBillingId');
  const canOverrideUmbrellaLimit = groups.includes('toolsCanOverrideUmbrellaLimit');
  const canRunCorrectiveActions = groups.includes('toolsCanRunCorrectiveActions');
  const canChangeOfferAffinity = groups.includes('toolsCanChangeOfferAffinity');
  const canAddViolationToOffer = groups.includes('toolsAddAutoViolationsOffer');
  const canUpdateStaffUsername = groups.includes('toolsCanUpdateStaffUsername');
  const canUpdateMortgage = groups.includes('toolsCanEditMortgage');
  const canRunBillingActions = groups.includes('toolsBillingActions');
  const canRunIvansActions = groups.includes('toolsIvansActions');
  const canBackfillData = groups.includes('toolsCanBackfillData');
  const canCreateCompaniesInRoutable = groups.includes('toolsCanCreateCompaniesInRoutable');
  const canSkipMatchingActivePolicySearch = groups.includes('toolsCanSkipMatchingActivePolicySearch');
  const canBulkOrderInspections = groups.includes('toolsCanBulkOrderInspections');
  const canOverrideBrandedTitle = groups.includes('toolsCanOverrideBrandedTitle');
  const canOverrideExcludedCar = groups.includes('toolsCanOverrideExcludedCar');
  const canOverrideMSRP = groups.includes('toolsCanOverrideMSRP');
  const canOverrideLoanNumVal = groups.includes('toolsCanOverrideLoanNumVal');
  const canOverrideApplicationSigningExpiry = groups.includes('toolsCanOverrideApplicationSigningExpiry');
  const canAllowAuxRating = groups.includes('toolsCanAllowAuxRating');
  const canBulkDownloadPolicyDocuments = groups.includes('toolsCanBulkDownloadPolicyDocuments');
  const canViewAiValidation = groups.includes('toolsOperationsActions');
  const canSeeHomeCharacteristicsDataset = groups.includes('toolsOperationsActions');
  const canAllowWaterBackup = groups.includes('toolsCanAllowWaterBackup');
  const canRefreshCharacteristicsAndRateOffer = groups.includes('toolsOperationsActions');
  const canRemoveAutoViolation = groups.includes('toolsRemoveAutoViolation');
  const canAddHomeClaim = groups.includes('toolsAddHomeClaimToOffer');
  const toolsOverrideReplacementCost = groups.includes('toolsOverrideReplacementCost');
  const canRetryOfferOptions = groups.includes('toolsCanRetryOfferOptions');
  const canSimulateSource = !isProductionEnv && groups.includes('toolsCanSimulateSource');
  const canManageDialpadCampaigns = groups.includes('toolsCanManageDialpadCampaigns');
  const canExportEasyKeyXML = groups.includes('CanExportEasyKeyXML');
  const toolAllOverridesAccess = groups.includes('toolsAllOverridesAccess');

  const canViewPolicyOverridesTool =
    toolAllOverridesAccess ||
    canOverrideUmbrellaLimit ||
    canOverrideBrandedTitle ||
    canOverrideExcludedCar ||
    canOverrideMSRP ||
    canOverrideApplicationSigningExpiry;
  const canViewOfferOverridesTool =
    toolAllOverridesAccess ||
    canOverrideLoanNumVal ||
    canAllowAuxRating ||
    canAllowWaterBackup ||
    canOverrideExcludedCar ||
    canOverrideMSRP ||
    toolsOverrideReplacementCost;

  return {
    // allows quoting, editing, and viewing offers
    [CognitoPermissionGroups.canQuote]:
      (isTeamLeader || isSales || isService || hasCanQuoteGroup || isUnlicensed) && !viewOnly,

    // allows purchasing offers/binding
    [CognitoPermissionGroups.canBind]: (isTeamLeader || isSales || isService) && !viewOnly,

    // allows viewing the checkout route but not necessarily binding
    [CognitoPermissionGroups.canViewCheckoutPage]:
      (isTeamLeader || isSales || isService || hasCanQuoteGroup) && !viewOnly,

    [CognitoPermissionGroups.canViewPolicies]: canViewPolicies,
    [CognitoPermissionGroups.canViewOffers]: canViewOffers,

    // if opening policy/offer for state not licensed for, the code should only allow unlicensed actions
    // which is determined via the permissions prop and useFormFieldPermissionHelpers used in FormField
    [CognitoPermissionGroups.canEditPolicies]: canEditPolicies,
    [CognitoPermissionGroups.canEditOffers]: canEditOffers,
    [CognitoPermissionGroups.canEdit]: canEditPolicies || canEditOffers,

    // allows viewing offers, policies, and customer, but does not allow editing, checkout, or modifying the data of any kind
    [CognitoPermissionGroups.viewOnly]: viewOnly,
    [CognitoPermissionGroups.isTeamLeader]: isTeamLeader && !viewOnly,
    [CognitoPermissionGroups.isSales]: isSales,
    [CognitoPermissionGroups.isService]: isService && !viewOnly,
    [CognitoPermissionGroups.isAgency]: isAgency,
    [CognitoPermissionGroups.isInternalAgent]: !isAgency,
    [CognitoPermissionGroups.canAccessProtectedUDDActions]: canAccessProtectedUDDActions,
    [CognitoPermissionGroups.isUnlicensed]: isUnlicensed,
    [CognitoPermissionGroups.canReinstate]: canReinstate,
    [CognitoPermissionGroups.canRescindCancellation]: canRescindCancellation,
    [CognitoPermissionGroups.canSeeFullOfferDetails]: canSeeFullOfferDetails,
    [CognitoPermissionGroups.isInternalSales]: isInternalSales && !viewOnly,
    [CognitoPermissionGroups.isExternalSales]: isExternalSales && !viewOnly,
    [CognitoPermissionGroups.canScrubIncidents]: canScrubIncidents,
    [CognitoPermissionGroups.canAddIncidents]: canAddIncidents,
    [CognitoPermissionGroups.canModifyAffinityAndLeadSource]: canModifyAffinityAndLeadSource,
    [CognitoPermissionGroups.canBackDate]: canBackDate,
    [CognitoPermissionGroups.canModifyBillingId]: canModifyBillingId && !isAgency,
    [CognitoPermissionGroups.canViewClarionDoorData]: canViewClarionDoorData,
    [CognitoPermissionGroups.canAddCarsManually]: canAddCarsManually && !isAgency,
    [CognitoPermissionGroups.canAutoRenew]: canAutoRenew && !isAgency,
    [CognitoPermissionGroups.canAddHoldCards]: canAddHoldCards,
    [CognitoPermissionGroups.canToggleEmployeeDiscount]: canToggleEmployeeDiscount && !isAgency,
    [CognitoPermissionGroups.canChangeExclusions]: canChangeExclusions,
    [CognitoPermissionGroups.canClearUDRs]: canClearUDRs,
    [CognitoPermissionGroups.canEditHomeAddress]: canEditHomeAddress,
    [CognitoPermissionGroups.canEditEffectiveDate]: canEditEffectiveDate,
    [CognitoPermissionGroups.canManuallyChargeFee]: canManuallyChargeFee,
    [CognitoPermissionGroups.canQuoteBranchPlus]: canQuoteBranchPlus,
    [CognitoPermissionGroups.canClearUWFormRejections]: canClearUWFormRejections,
    [CognitoPermissionGroups.canUploadDocuments]:
      (isInternalSales || isExternalSales || isService || isTeamLeader || isUnlicensed || (!isBranchEnv && isAgency)) &&
      !viewOnly,
    [CognitoPermissionGroups.canDeleteInternalDocuments]: canDeleteInternalDocuments,
    [CognitoPermissionGroups.canOverrideAllRejections]: canOverrideAllRejections,
    [CognitoPermissionGroups.canManuallyChargePolicy]: canManuallyChargePolicy,
    [CognitoPermissionGroups.canRewritePolicy]: canRewritePolicy,
    [CognitoPermissionGroups.canRemoveExcludedDriver]: canRemoveExcludedDriver,
    [CognitoPermissionGroups.canCancelReasonCNIP]: canCancelReasonCNIP,
    [CognitoPermissionGroups.canCancelReasonCNMS]: canCancelReasonCNMS,
    [CognitoPermissionGroups.canCancelReasonCNPY_X]: canCancelReasonCNPY_X,
    [CognitoPermissionGroups.canRejectPIP]: canRejectPIP,
    // Mutual brands show the Portal to every user via the
    // `agencyPortalVisibleToAllUsers` brand feature flag. (#31094, #33024)
    [CognitoPermissionGroups.showAgencyPortal]: resolveShowAgencyPortal({
      showAgencyPortal,
      isAgency,
      agencyPortalVisibleToAllUsers: !!appConfig?.featureFlags?.agencyPortalVisibleToAllUsers
    }),
    [CognitoPermissionGroups.canDownloadAnonDocs]: canDownloadAnonDocs,
    [CognitoPermissionGroups.canReinstateCNPY]: canReinstateCNPY,
    [CognitoPermissionGroups.canToggleCreditCheck]: canToggleCreditCheck,
    [CognitoPermissionGroups.canEditBillingDay]: canEditBillingDay,
    [CognitoPermissionGroups.canToggleCHDiscount]: canToggleCHDiscount,
    [CognitoPermissionGroups.canToggleInventoryDiscount]: canToggleInventoryDiscount,
    [CognitoPermissionGroups.canEditPolicyMPD]: canEditPolicyMPD,
    [CognitoPermissionGroups.canDelayPaymentOrRefund]: canDelayPaymentOrRefund,
    [CognitoPermissionGroups.canManageBillingHolds]: canManageBillingHolds,
    [CognitoPermissionGroups.canSendPaperDocs]: canSendPaperDocs,
    [CognitoPermissionGroups.canEditFeeCheckbox]: canEditFeeCheckbox,
    [CognitoPermissionGroups.canOverrideGaragingAddress]: canOverrideGaragingAddress,
    [CognitoPermissionGroups.canSwapApplicants]: canSwapApplicants,
    [CognitoPermissionGroups.canViewCarrierInitiatedCancels]: canViewCarrierInitiatedCancels,
    [CognitoPermissionGroups.canSellAndService]: canSellAndService,
    [CognitoPermissionGroups.canCancelReasonNACP]: canCancelReasonNACP,
    [CognitoPermissionGroups.canEditUneditablePropertyDetailsFields]: canEditUneditablePropertyDetailsFields,
    [CognitoPermissionGroups.canResetOnboardingTasks]: canResetOnboardingTasks,
    [CognitoPermissionGroups.canCancelOnboardingTasks]: canCancelOnboardingTasks,
    [CognitoPermissionGroups.canViewInternalTasks]: canViewInternalTasks,
    [CognitoPermissionGroups.canViewUWCancelCodes]: canViewUWCancelCodes,
    [CognitoPermissionGroups.canToggleIsAgencyPolicy]: canToggleIsAgencyPolicy,
    [CognitoPermissionGroups.canRefundStripePayment]: canRefundStripePayment,
    [CognitoPermissionGroups.canViewTools]: canViewTools,
    [CognitoPermissionGroups.canViewAffinityTools]: canViewAffinityTools,
    [CognitoPermissionGroups.canViewStaffDashboard]: canViewStaffDashboard,
    [CognitoPermissionGroups.canGenerateDescopePasskeyReport]: canGenerateDescopePasskeyReport,
    [CognitoPermissionGroups.canResetStaffUserPassword]: canResetStaffUserPassword,
    [CognitoPermissionGroups.toolsDeleteRenewalSegment]: toolsDeleteRenewalSegment,
    [CognitoPermissionGroups.toolsCreateUddReviewTask]: toolsCreateUddReviewTask,
    [CognitoPermissionGroups.canCreateStripeCharge]: canCreateStripeCharge,
    [CognitoPermissionGroups.canCreateChargedeskInvoice]: canCreateChargedeskInvoice,
    [CognitoPermissionGroups.canBulkDisableStaffUsers]: canBulkDisableStaffUsers,
    [CognitoPermissionGroups.canCreateChargedeskRefundInvoice]: canCreateChargedeskRefundInvoice,
    [CognitoPermissionGroups.canRegeneratePolicyForm]: canRegeneratePolicyForm,
    [CognitoPermissionGroups.canUpdateChargedeskRecord]: canUpdateChargedeskRecord,
    [CognitoPermissionGroups.canUpdateChargedeskRecordPolicyOrBillingId]: canUpdateChargedeskRecordPolicyOrBillingId,
    [CognitoPermissionGroups.canGenerateRenewal]: canGenerateRenewal,
    [CognitoPermissionGroups.canRunRenewalUnderwriting]: canRunRenewalUnderwriting,
    [CognitoPermissionGroups.canUpdateStaffUser]: canUpdateStaffUser,
    [CognitoPermissionGroups.canDisableStaffUser]: canDisableStaffUser,
    [CognitoPermissionGroups.canViewEditGroups]: canViewEditGroups,
    [CognitoPermissionGroups.canRunAddressesThroughCherre]: canRunAddressesThroughCherre,
    [CognitoPermissionGroups.canUpdatePolicyRep]: canUpdatePolicyRep,
    [CognitoPermissionGroups.canAddPermissionGroupsToStaffUsers]: canAddPermissionGroups,
    [CognitoPermissionGroups.canCreateStaffUser]: canCreateStaffUser,
    [CognitoPermissionGroups.canCreateFrontTicket]: canCreateFrontTicket,
    [CognitoPermissionGroups.canViewStateRateChangeDashboard]: canViewStateRateChangeDashboard,
    [CognitoPermissionGroups.canViewMoratoriumDashboard]: canViewMoratoriumDashboard,
    [CognitoPermissionGroups.canImportPoliciesToSnapsheet]: canImportPoliciesToSnapsheet,
    [CognitoPermissionGroups.canImportPoliciesToIvans]: canImportPoliciesToIvans,
    [CognitoPermissionGroups.canRegeneratePolicyCancelForm]: canRegeneratePolicyCancelForm,
    [CognitoPermissionGroups.canAddCatTagsToClaims]: canAddCatTagsToClaims,
    [CognitoPermissionGroups.canRunTechTasks]: canRunTechTasks,
    [CognitoPermissionGroups.canRescindOrReinstateGSNICPolicy]: canRescindOrReinstateGSNICPolicy,
    [CognitoPermissionGroups.canRemoveFinalPaymentMethod]: canRemoveFinalPaymentMethod,
    [CognitoPermissionGroups.canCopyPolicy]: canCopyPolicy,
    [CognitoPermissionGroups.canSetMemberAffinity]: canSetMemberAffinity,
    [CognitoPermissionGroups.canEnableStaffUser]: canEnableStaffUser,
    [CognitoPermissionGroups.canResetPasskeys]: canResetPasskeys,
    [CognitoPermissionGroups.canPullDrivingHistoryFromCSV]: canPullDrivingHistoryFromCSV,
    [CognitoPermissionGroups.canSetHasAutoPolicy]: canSetHasAutoPolicy,
    [CognitoPermissionGroups.canUpdateBillingId]: canUpdateBillingId,
    [CognitoPermissionGroups.toolAllOverridesAccess]: toolAllOverridesAccess,
    [CognitoPermissionGroups.canOverrideUmbrellaLimit]: canOverrideUmbrellaLimit,
    [CognitoPermissionGroups.canOverrideBrandedTitle]: canOverrideBrandedTitle,
    [CognitoPermissionGroups.canOverrideMSRP]: canOverrideMSRP,
    [CognitoPermissionGroups.canOverrideExcludedCar]: canOverrideExcludedCar,
    [CognitoPermissionGroups.canOverrideApplicationSigningExpiry]: canOverrideApplicationSigningExpiry,
    [CognitoPermissionGroups.canBypassDriversLicenseCheck]: canBypassDriversLicenseCheck,
    [CognitoPermissionGroups.canRunCorrectiveActions]: canRunCorrectiveActions,
    [CognitoPermissionGroups.canChangeOfferAffinity]: canChangeOfferAffinity,
    [CognitoPermissionGroups.canAddViolationToOffer]: canAddViolationToOffer,
    [CognitoPermissionGroups.canUpdateStaffUsername]: canUpdateStaffUsername,
    [CognitoPermissionGroups.canEditCoverageA]: canEditCoverageA,
    [CognitoPermissionGroups.canSeeMarketTierCard]: canSeeMarketTierCard,
    [CognitoPermissionGroups.canWriteMultipleAffinityCodes]: canWriteMultipleAffinityCodes,
    [CognitoPermissionGroups.canEditDriversName]: canEditDriversName,
    [CognitoPermissionGroups.canUpdateMortgage]: canUpdateMortgage,
    [CognitoPermissionGroups.canRunBillingActions]: canRunBillingActions,
    [CognitoPermissionGroups.canRunIvansActions]: canRunIvansActions,
    [CognitoPermissionGroups.canModifyNonRatedIndividual]: canModifyNonRatedIndividual,
    [CognitoPermissionGroups.canBackfillData]: canBackfillData,
    [CognitoPermissionGroups.canCreateCompaniesInRoutable]: canCreateCompaniesInRoutable,
    [CognitoPermissionGroups.canSkipMatchingActivePolicySearch]: canSkipMatchingActivePolicySearch,
    [CognitoPermissionGroups.canUploadTaskCenterDocuments]:
      (isInternalSales || isExternalSales || isService || isTeamLeader || isUnlicensed) && !viewOnly,
    [CognitoPermissionGroups.canViewPolicyOverridesTool]: canViewPolicyOverridesTool,
    [CognitoPermissionGroups.canRevertPolicyDetails]: canRevertPolicyDetails,
    [CognitoPermissionGroups.canBulkOrderInspections]: canBulkOrderInspections,
    [CognitoPermissionGroups.canOverrideLoanNumVal]: canOverrideLoanNumVal,
    [CognitoPermissionGroups.canAllowAuxRating]: canAllowAuxRating,
    [CognitoPermissionGroups.canViewOfferOverridesTool]: canViewOfferOverridesTool,
    [CognitoPermissionGroups.canToggleHomeRepairsRequested]: canToggleHomeRepairsRequested,
    [CognitoPermissionGroups.canBulkDownloadPolicyDocuments]: canBulkDownloadPolicyDocuments,
    [CognitoPermissionGroups.canAllowWaterBackup]: canAllowWaterBackup,
    [CognitoPermissionGroups.canViewAiValidation]: canViewAiValidation || isTeamLeader,
    [CognitoPermissionGroups.canSeeHomeCharacteristicsDataset]: canSeeHomeCharacteristicsDataset,
    [CognitoPermissionGroups.canRefreshCharacteristicsAndRateOffer]: canRefreshCharacteristicsAndRateOffer,
    [CognitoPermissionGroups.canEditPriorCarrierDetails]: canEditPriorCarrierDetails,
    [CognitoPermissionGroups.canSkipOfferUnderwritingRules]: canSkipOfferUnderwritingRules,
    [CognitoPermissionGroups.canEditFarmPolicyAdjustment]: canEditFarmPolicyAdjustment,
    [CognitoPermissionGroups.canApproveUWReview]: canApproveUWReview,
    [CognitoPermissionGroups.canBindDirectly]: canBindDirectly,
    [CognitoPermissionGroups.canRemoveAutoViolation]: canRemoveAutoViolation,
    [CognitoPermissionGroups.canAddHomeClaim]: canAddHomeClaim,
    [CognitoPermissionGroups.toolsOverrideReplacementCost]: toolsOverrideReplacementCost,
    [CognitoPermissionGroups.canRetryOfferOptions]: canRetryOfferOptions,
    [CognitoPermissionGroups.canSimulateSource]: canSimulateSource,
    [CognitoPermissionGroups.canManageDialpadCampaigns]: canManageDialpadCampaigns,
    [CognitoPermissionGroups.canExportEasyKeyXML]: canExportEasyKeyXML
  };
}

const defaultState = {
  user: null,
  permissionGroups: [],
  isLoggedIn: false,
  updateEmail: async (emailAddress) => {
    await updateUserAttribute({
      userAttribute: {
        attributeKey: 'email',
        value: emailAddress
      }
    });
  },
  loading: true,
  userPreferences: null
};

const GET_USER_PREFERENCES = gql`
  query {
    getUserPreferences {
      username
      affinityGroups
      meiliSearchKey
      meiliSearchHost
      searchEngine
      legacyAffinityCode
      searchMode
    }
  }
`;

class Auth extends React.Component {
  static contextType = MobXProviderContext; // This sets `this.context` to be the MobX store.
  static propTypes = {
    children: PropTypes.node.isRequired,
    client: PropTypes.object.isRequired,
    toast: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    Hub.listen('auth', async ({ payload }) => {
      const { event } = payload;
      if (event === 'signedIn') {
        try {
          const sessionData = await this.setupUserSession();

          if (awsConfig.trackingEnabled) {
            if (sessionData?.user?.username) {
              window.sessionStorage.setItem('branchSessionUsername', `staff-${sessionData?.user?.username}`);
            }
            if (sessionData?.user?.email) {
              window.sessionStorage.setItem('branchSessionEmail', sessionData.user.email);
            }
            track('Staff Sign In', {
              username: sessionData.user.username,
              email: sessionData.user.email
            });
          }

          const deepLink = oAuthStorage.getDeepLink();
          if (deepLink) {
            window.location.href = deepLink;
          }
        } catch (error) {
          console.error('Error handling signedIn event:', error);
        }
      }

      if (event === 'signedOut') {
        window.location.reload();
      }
    });
    this.state = defaultState;
    // Bound so they can be exposed through AuthContext (consumed by the account-menu
    // Permissions modal — https://github.com/gobranch/branch/issues/31758)
    this.checkForPermissionChanges = this.checkForPermissionChanges.bind(this);
    this.applyRefreshedPermissions = this.applyRefreshedPermissions.bind(this);
  }

  async componentDidMount() {
    try {
      this.setState({ loading: true });
      await this.setupUserSession();
      oAuthStorage.deleteInitiatedAuthStatus();
    } catch (e) {
      console.log('Error getting session from Amplify Auth: ', e);
      this.setState({ ...defaultState, loading: false });
      oAuthStorage.deleteInitiatedAuthStatus();
    }
  }

  async getUserPreferences() {
    const { client } = this.props;
    try {
      const {
        data: { getUserPreferences }
      } = await client.query({ query: GET_USER_PREFERENCES });
      return {
        affinityGroups: getUserPreferences?.affinityGroups,
        meiliSearchKey: getUserPreferences?.meiliSearchKey,
        meiliSearchHost: getUserPreferences?.meiliSearchHost,
        searchEngine: getUserPreferences?.searchEngine,
        legacyAffinityCode: getUserPreferences?.legacyAffinityCode,
        searchMode: getUserPreferences?.searchMode
      };
    } catch {
      console.warn('Try deploying dynamoDB/staff-appsync-be to have a valid user preferences response.');
      return { affinityGroups: undefined, meiliSearchKey: undefined };
    }
  }

  // Pure function - builds complete session data with no side effects
  buildSessionData({ user, userAttributes, userPreferences }) {
    // Parse custom attributes helper
    const parseCustomAttribute = (attr) => {
      try {
        return JSON.parse(attr);
      } catch {
        return [];
      }
    };

    // Parse and clean allowed/unlicensed states
    const parsedAllowedStates = parseCustomAttribute(userAttributes['custom:allowed_states']);
    const parsedUnlicensedStates = parseCustomAttribute(userAttributes['custom:unlicensed_states']);

    const licensedStates = parsedAllowedStates?.length
      ? parsedAllowedStates.filter((state) => state?.trim() !== '')
      : [];
    const unlicensedStates = parsedUnlicensedStates?.length
      ? parsedUnlicensedStates.filter((state) => state?.trim() !== '' && licensedStates.indexOf(state) === -1)
      : [];

    return {
      // User data
      user: {
        username: user.username,
        groups: userAttributes['cognito:groups'] || [],
        email: userAttributes.email,
        firstName: userAttributes.given_name,
        lastName: userAttributes.family_name,
        phoneNumber: userAttributes.phone_number,
        affinityCode: userAttributes['custom:affinity_code'],
        affinityStems: userAttributes['custom:affinity_group_regex'],
        viewableAffinityCodes:
          userAttributes['custom:affinity_groups']?.trim()?.length > 0
            ? parseCustomAttribute(userAttributes['custom:affinity_groups'])
            : [],
        licenseNumber: userAttributes['custom:license_number'],
        agencyRole: userAttributes['custom:agency_role']
      },
      // Auth state
      isLoggedIn: true,
      loading: false,
      // Permissions (spread all permission flags)
      ...getPermissions(userAttributes),
      // States
      allowedStates: licensedStates,
      unlicensedStates,
      allStates: [...(parsedAllowedStates || []), ...(parsedUnlicensedStates || [])],
      userPreferences
    };
  }

  // Step 1 of the two-step permission refresh: force-refresh the Cognito tokens (the
  // refresh token mints a new ID token, picking up cognito:groups changes made since
  // login) and report what changed — WITHOUT touching React state. Callers decide when
  // to apply, because applying a removal can hard-redirect the user off a ProtectedRoute
  // (protected-route.jsx sets window.location.href when its permission flips false).
  async checkForPermissionChanges() {
    const currentGroups = this.state.user?.groups || [];
    const authSession = await fetchAuthSession({ forceRefresh: true });
    // No tokens back means the refresh token itself is expired/invalid. Throw (the modal
    // shows its re-login error state) instead of diffing against an empty list, which
    // would misreport every group as removed.
    const idTokenPayload = authSession.tokens?.idToken?.payload;
    if (!idTokenPayload) {
      throw new Error('No tokens returned from forced session refresh');
    }
    const newGroups = idTokenPayload['cognito:groups'] || [];
    return diffPermissionGroups(currentGroups, newGroups);
  }

  // Step 2: rebuild the live session from the (already force-refreshed) tokens so every
  // useSession consumer — route guards, tool gating, form-field permissions — picks up
  // the new groups without a re-login. Skips third-party identify re-runs: identity
  // didn't change, and FrontChat('init') is not guarded against double-initialization.
  async applyRefreshedPermissions() {
    await this.setupUserSession({ skipIntegrations: true });
  }

  async setupUserSession({ skipIntegrations = false } = {}) {
    // 1. Fetch raw auth data
    const [user, authSession, userPreferences] = await Promise.all([
      getCurrentUser(),
      fetchAuthSession(),
      this.getUserPreferences()
    ]);

    // Extract user attributes from ID token payload (includes cognito:groups)
    // What you get back from fetchUserAttributes does NOT include cognito:groups for some reason.
    const userAttributes = authSession.tokens.idToken.payload;

    // 2. Build session data (pure function)
    const sessionData = this.buildSessionData({ user, userAttributes, userPreferences });

    // 3. Update auth state
    this.context.store?.userPreferences.setUserPreferences(sessionData.userPreferences);
    this.setState(sessionData);

    if (skipIntegrations) {
      return sessionData;
    }

    // 4. Use session data for external integrations
    if (isBranchEnv) {
      initializeFrontChatAndIdentify(sessionData);
    }
    identifyUserInUpscope(sessionData.user);
    identifyUserInAppcues(sessionData);

    // 5. Analytics tracking
    // Fetch spin-the-wheel data for Appcues user properties (Branch brand only)
    let isEligibleForSpinTheWheel = false;
    let hasSpunWheelBefore = false;
    console.log({ isBranchEnv });
    if (isBranchEnv) {
      try {
        const { client } = this.props;
        const [eligibilityResult, historyResult] = await Promise.allSettled([
          sessionData.user.email
            ? client.query({
                query: GET_SPIN_THE_WHEEL_ELIGIBILITY,
                variables: { email: sessionData.user.email }
              })
            : Promise.resolve(null),
          sessionData.user.username
            ? client.query({
                query: GET_SPIN_THE_WHEEL_HISTORY,
                variables: { rep: sessionData.user.username }
              })
            : Promise.resolve(null)
        ]);

        isEligibleForSpinTheWheel =
          eligibilityResult.status === 'fulfilled' &&
          eligibilityResult.value?.data?.getSpinTheWheelEligibility?.eligible === true;
        hasSpunWheelBefore =
          historyResult.status === 'fulfilled' &&
          historyResult.value?.data?.getSpinTheWheelHistory?.hasSpunBefore === true;
      } catch (error) {
        console.warn('Error fetching spin-the-wheel data for identify:', error);
      }
    }

    await identify({
      username: sessionData.user.username,
      email: sessionData.user.email,
      isAgency: sessionData.isAgency,
      canSellAndService: sessionData.canSellAndService,
      firstName: sessionData.user.firstName || null,
      lastName: sessionData.user.lastName || null,
      agencyRole: sessionData.user.agencyRole || null,
      licensedStates: sessionData.allowedStates?.join(',') || null,
      unlicensedStates: sessionData.unlicensedStates?.join(',') || null,
      viewableAffinityCodes: sessionData.user.viewableAffinityCodes.join(',') || null,
      isEligibleForSpinTheWheel,
      hasSpunWheelBefore
    });

    return sessionData;
  }

  render() {
    const { children } = this.props;

    return (
      <DescopeProvider
        projectId={import.meta.env.VITE_STAFF_DESCOPE_PROJECT_ID}
        {...(isProductionEnv && {
          baseUrl: import.meta.env.VITE_STAFF_DESCOPE_BASE_URL,
          sessionTokenViaCookie: true
        })}
      >
        <AuthContext.Provider
          value={{
            ...this.state,
            checkForPermissionChanges: this.checkForPermissionChanges,
            applyRefreshedPermissions: this.applyRefreshedPermissions
          }}
        >
          {children}
        </AuthContext.Provider>
      </DescopeProvider>
    );
  }
}

export default withToast(Auth);
